import "./TermsOfService.css";
import { Link } from "react-router-dom";
import icon from "../../assets/icon128.png";

const EFFECTIVE_DATE = "March 20, 2026";

export default function TermsOfService() {
  document.title = "Profile Connector";
  return (
    <div className="tos-root">
      {/* Nav */}
      <nav className="tos-nav">
        <Link to="/landing" className="tos-nav-brand">
          <img src={icon} alt="Profile Connector" className="tos-nav-logo" />
          Profile Connector
        </Link>
        <Link to="/landing" className="tos-nav-back">
          ← Back to Home
        </Link>
      </nav>

      {/* Hero */}
      <div className="tos-hero">
        <div className="tos-hero-badge">📄 Legal</div>
        <h1>Terms of Service</h1>
        <p className="tos-hero-meta">
          Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="tos-layout">
        {/* Table of Contents */}
        <aside className="tos-toc">
          <div className="tos-toc-title">On this page</div>
          <ul>
            <li><a href="#acceptance">1. Acceptance of Terms</a></li>
            <li><a href="#description">2. Description of Service</a></li>
            <li><a href="#what-we-do">3. What the Extension Does</a></li>
            <li><a href="#what-we-dont">4. What We Don't Do</a></li>
            <li><a href="#linkedin-compliance">5. LinkedIn Compliance</a></li>
            <li><a href="#user-obligations">6. User Obligations</a></li>
            <li><a href="#data-privacy">7. Data & Privacy (Limited Use)</a></li>
            <li><a href="#permissions">8. Extension Permissions</a></li>
            <li><a href="#user-control">9. User Control</a></li>
            <li><a href="#ip">10. Intellectual Property</a></li>
            <li><a href="#disclaimers">11. Disclaimers</a></li>
            <li><a href="#liability">12. Limitation of Liability</a></li>
            <li><a href="#termination">13. Termination</a></li>
            <li><a href="#changes">14. Changes to Terms</a></li>
            <li><a href="#governing-law">15. Governing Law</a></li>
            <li><a href="#contact">16. Contact Us</a></li>
          </ul>
        </aside>

        {/* Content */}
        <main className="tos-content">
          <div className="tos-highlight">
            <p>
              Please read these Terms of Service carefully before installing or
              using the Profile Connector Chrome extension. By installing or using
              the extension, you agree to be bound by these terms.
            </p>
          </div>

          <section className="tos-section" id="acceptance">
            <h2>1. Acceptance of Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding
              agreement between you ("User," "you," or "your") and Profile
              Connector ("we," "us," or "our") governing your access to and use
              of the Profile Connector Chrome browser extension and any related
              services (collectively, the "Service").
            </p>
            <p>
              By installing the extension from the Chrome Web Store, clicking
              "Add to Chrome," or otherwise using the Service, you acknowledge
              that you have read, understood, and agree to be bound by these
              Terms. If you do not agree to these Terms, you must not install or
              use the Service.
            </p>
            <p>
              You represent that you are at least 18 years of age and have the
              legal authority to enter into this agreement.
            </p>
            <p>
              <strong>Chrome Web Store Policy Hierarchy:</strong> These Terms
              are subject to and do not supersede the{" "}
              <strong>Google Chrome Web Store Developer Agreement</strong> and
              the <strong>Chrome Web Store Program Policies</strong>, which take
              precedence over these Terms to the extent of any conflict.
            </p>
          </section>

          <section className="tos-section" id="description">
            <h2>2. Description of Service</h2>
            <p>
              Profile Connector is a Google Chrome browser extension designed
              exclusively to allow users to authorize the connection of their
              existing LinkedIn account to a third-party partner platform or
              dashboard that has integrated with the Profile Connector standard.
            </p>
            <p>
              The Service acts solely as a secure authentication bridge. It
              enables partner platforms to confirm the identity of a LinkedIn
              user based on an active browser session that the user themselves
              has created by logging into LinkedIn.
            </p>
            <p>
              Profile Connector is not affiliated with, endorsed by, or
              sponsored by LinkedIn Corporation or its parent company Microsoft.
            </p>
          </section>

          <section className="tos-section" id="what-we-do">
            <h2>3. What the Extension Does</h2>
            <p>
              When you activate the Profile Connector extension by clicking the
              "Connect via Extension" button in a compatible partner dashboard,
              the extension performs the following limited actions:
            </p>
            <ul>
              <li>
                Detects the presence of an active LinkedIn login session in
                your current Chrome browser.
              </li>
              <li>
                Reads the session token associated with your active LinkedIn
                session — the same token your browser uses to keep you logged
                in.
              </li>
              <li>
                Transmits that session token securely (via TLS 1.3 encryption)
                to the partner platform you are attempting to connect to, for
                the sole purpose of verifying your LinkedIn account identity.
              </li>
              <li>
                Displays a confirmation message once the connection is
                successfully established.
              </li>
            </ul>
            <p>
              All of the above actions are performed <strong>only</strong> when
              you explicitly click the Connect button. The extension does not
              run in the background or perform any actions without your direct
              initiation.
            </p>
          </section>

          <section className="tos-section" id="what-we-dont">
            <h2>4. What We Don't Do</h2>
            <p>
              We are committed to transparency. The following is an explicit list
              of things Profile Connector does <strong>not</strong> do:
            </p>
            <ul>
              <li>
                <strong>We do not scrape data.</strong> We do not extract,
                collect, or transmit your LinkedIn profile information,
                connections, messages, posts, job history, contact details, or
                any other content from LinkedIn pages.
              </li>
              <li>
                <strong>We do not automate actions.</strong> We do not send
                connection requests, messages, InMails, endorsements, reactions,
                or perform any action on LinkedIn on your behalf.
              </li>
              <li>
                <strong>We do not crawl or index.</strong> We do not browse,
                crawl, spider, or index any LinkedIn content programmatically.
              </li>
              <li>
                <strong>We do not store your credentials.</strong> We never ask
                for, receive, or store your LinkedIn email address or password.
              </li>
              <li>
                <strong>We do not sell your data.</strong> We do not sell,
                rent, lease, or otherwise disclose any data about you to third
                parties for commercial purposes.
              </li>
              <li>
                <strong>We do not run background scripts.</strong> The extension
                performs no background activity when you are not actively using
                the Connect feature.
              </li>
              <li>
                <strong>We do not access your other accounts.</strong> The
                extension only interacts with LinkedIn sessions and performs no
                actions on any other website or service.
              </li>
              <li>
                <strong>We do not mine cryptocurrency.</strong> The extension
                performs no computational tasks unrelated to its stated purpose.
              </li>
              <li>
                <strong>We do not circumvent paywalls or authentication.</strong>{" "}
                The extension does not bypass, defeat, or circumvent any access
                controls, payment systems, or authentication mechanisms on any
                website or service.
              </li>
              <li>
                <strong>We do not inject malware or malicious code.</strong> The
                extension does not install, transmit, or execute any malware,
                spyware, ransomware, viruses, or other harmful software.
              </li>
              <li>
                <strong>We do not require ad clicks or data submission.</strong>{" "}
                We never require you to click advertisements, complete surveys,
                or submit personal information as a condition of using the
                extension's core functionality.
              </li>
              <li>
                <strong>We do not use data for advertising.</strong> We do not
                transfer any data to advertising platforms or use data for
                personalized, retargeted, or interest-based advertising.
              </li>
            </ul>
          </section>

          <section className="tos-section" id="linkedin-compliance">
            <h2>5. LinkedIn Compliance</h2>
            <p>
              Profile Connector is designed to comply with{" "}
              <strong>LinkedIn's User Agreement and Professional Community Policies</strong>.
              Specifically:
            </p>

            <h3>5.1 No Violation of LinkedIn's Terms</h3>
            <p>
              The extension does not violate LinkedIn's prohibitions on
              scraping, automated data extraction, or the use of robots, spiders,
              or other automated devices to access LinkedIn services. The
              extension reads a browser session token that exists solely as a
              result of the user's own manual login — it performs no automated
              interactions with LinkedIn's servers or interfaces.
            </p>

            <h3>5.2 User-Initiated Actions Only</h3>
            <p>
              Every operation performed by Profile Connector is explicitly
              initiated by you, the user. The extension does not act
              autonomously. This is consistent with LinkedIn's requirement that
              users interact with the platform in ways that are genuine and
              human-directed.
            </p>

            <h3>5.3 No Data Mining or Aggregation</h3>
            <p>
              We do not engage in data mining, harvesting, or any aggregation of
              LinkedIn user data. The sole data element accessed is the session
              token required to authenticate your identity with the partner
              platform.
            </p>

            <div className="tos-warn">
              <p>
                ⚠️ While Profile Connector is designed to comply with LinkedIn's
                Terms of Service, we cannot guarantee that LinkedIn will not
                change its policies in ways that affect this extension. It is
                your responsibility to ensure that your use of this extension
                remains consistent with LinkedIn's current terms.
              </p>
            </div>
          </section>

          <section className="tos-section" id="user-obligations">
            <h2>6. User Obligations</h2>
            <p>By using Profile Connector, you agree to:</p>
            <ul>
              <li>
                Use the Service only to connect LinkedIn accounts that you own
                or have explicit authorization to connect.
              </li>
              <li>
                Not attempt to reverse engineer, decompile, or modify the
                extension for purposes other than those described in these Terms.
              </li>
              <li>
                Not use the Service for any unlawful purpose or in violation of
                any applicable laws or regulations.
              </li>
              <li>
                Comply with LinkedIn's User Agreement and Professional Community
                Policies at all times.
              </li>
              <li>
                Comply with the terms of service of the partner platform to
                which you are connecting your account.
              </li>
              <li>
                Not use the Service to impersonate another person or entity.
              </li>
              <li>
                Promptly report any security vulnerabilities or unexpected
                behavior to us via the contact information below.
              </li>
            </ul>
          </section>

          <section className="tos-section" id="data-privacy">
            <h2>7. Data & Privacy</h2>

            <h3 id="privacy">7.1 Personal and Sensitive Data</h3>
            <p>
              The LinkedIn session token stored in your browser's cookie store
              constitutes <strong>personal and sensitive user data</strong> as
              classified by the Chrome Web Store User Data Policy. We handle it
              accordingly:
            </p>
            <ul>
              <li>
                Transmitted directly and securely (TLS 1.3) to the partner
                platform you are connecting to, and to no other party.
              </li>
              <li>
                Not stored, logged, or retained by Profile Connector or its
                servers beyond the duration of the connection handshake.
              </li>
              <li>
                Used only for the single purpose of verifying your LinkedIn
                account identity with the partner platform you have explicitly
                chosen to connect with.
              </li>
            </ul>

            <h3>7.2 Limited Use</h3>
            <p>
              Data accessed by this extension is used exclusively to provide
              and improve the extension's single stated purpose: securely
              connecting your LinkedIn account to a compatible partner platform.
              We do not use this data for any secondary purpose, including
              advertising, profiling, or resale. Our data practices comply with
              the Chrome Web Store User Data Policy, including its Limited Use
              requirements.
            </p>

            <h3>7.3 No Personal Data Collection</h3>
            <p>
              Profile Connector does not collect, store, or process personally
              identifiable information (PII) such as your name, email address,
              phone number, or LinkedIn profile URL.
            </p>

            <h3>7.4 Analytics</h3>
            <p>
              The extension may collect anonymized, non-identifying usage
              statistics (such as whether a connection attempt succeeded or
              failed) to help us improve reliability. These statistics contain
              no personal information and cannot be used to identify you.
            </p>

            <h3>7.5 Third-Party Partner Platforms</h3>
            <p>
              Once your LinkedIn session token is transmitted to a partner
              platform, your data is subject to that platform's own privacy
              policy and terms of service. We encourage you to review the
              partner platform's privacy practices before connecting your
              account.
            </p>

            <h3>7.6 Full Privacy Policy</h3>
            <p>
              For complete details on how we handle data, including error
              reporting and service provider relationships, please review our{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section className="tos-section" id="permissions">
            <h2>8. Extension Permissions</h2>
            <p>
              Profile Connector requests only the minimum Chrome permissions
              necessary to provide the Service:
            </p>
            <ul>
              <li>
                <strong>activeTab:</strong> Required to detect an active LinkedIn
                session in the current browser tab when you initiate a
                connection.
              </li>
              <li>
                <strong>cookies (linkedin.com):</strong> Required to read the
                LinkedIn session token from your browser's cookie store for the
                linkedin.com domain only. No cookies from other domains are
                accessed.
              </li>
            </ul>
            <p>
              We do not request permissions to access browsing history, read
              other websites, inject scripts into pages, or access any system
              resources beyond those listed above.
            </p>
          </section>

          <section className="tos-section" id="user-control">
            <h2>9. User Control</h2>
            <p>You are always in control:</p>
            <ul>
              <li>
                The Extension only operates when you actively initiate a
                connection.
              </li>
              <li>
                You may disconnect your account at any time through the
                supported application.
              </li>
            </ul>
          </section>

          <section className="tos-section" id="ip">
            <h2>10. Intellectual Property</h2>
            <p>
              The Profile Connector extension, including its source code, design,
              branding, and documentation, is the intellectual property of
              Profile Connector and is protected by applicable copyright and
              intellectual property laws.
            </p>
            <p>
              You are granted a limited, non-exclusive, non-transferable,
              revocable license to install and use the extension solely for the
              purposes described in these Terms. This license does not include
              the right to sublicense, sell, or commercially exploit the
              extension.
            </p>
          </section>

          <section className="tos-section" id="disclaimers">
            <h2>11. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT
              NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the Service will be uninterrupted,
              error-free, or free of viruses or other harmful components. We do
              not warrant that the Service will always be compatible with
              LinkedIn's platform, as LinkedIn may change its technical
              infrastructure at any time.
            </p>
            <p>
              Profile Connector is not affiliated with, endorsed by, or sponsored
              by LinkedIn Corporation or Microsoft. LinkedIn is a registered
              trademark of LinkedIn Corporation.
            </p>
          </section>

          <section className="tos-section" id="liability">
            <h2>12. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
              SHALL PROFILE CONNECTOR, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR
              AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO
              YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE
              POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p>
              OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
              RELATED TO THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE
              AMOUNT YOU PAID FOR THE SERVICE IN THE TWELVE MONTHS PRECEDING
              THE CLAIM, OR (B) $50 USD.
            </p>
            <p>
              Some jurisdictions do not allow the exclusion or limitation of
              liability for certain damages, so the above limitation may not
              apply to you.
            </p>
          </section>

          <section className="tos-section" id="termination">
            <h2>13. Termination</h2>
            <p>
              You may terminate your use of the Service at any time by
              uninstalling the Profile Connector extension from your Chrome
              browser.
            </p>
            <p>
              We reserve the right to suspend or terminate your access to the
              Service at any time, with or without notice, if we believe you
              have violated these Terms or applicable law.
            </p>
            <p>
              Upon termination, all licenses granted to you under these Terms
              will immediately cease. Sections 10, 11, 12, and 15 shall survive
              termination.
            </p>
          </section>

          <section className="tos-section" id="changes">
            <h2>14. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. When we
              make material changes, we will update the "Effective date" at the
              top of this page and, where appropriate, notify you through the
              Chrome Web Store listing or within the extension itself.
            </p>
            <p>
              Your continued use of the Service after any changes to these Terms
              constitutes your acceptance of the revised Terms. If you do not
              agree to the revised Terms, you must discontinue use of the
              Service.
            </p>
            <p>
              We encourage you to review these Terms periodically to stay
              informed of any updates.
            </p>
          </section>

          <section className="tos-section" id="governing-law">
            <h2>15. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with
              the laws of the European Union, without regard to conflict of law
              provisions. Any dispute arising under these Terms that cannot be
              resolved informally shall be submitted to the competent courts of
              the jurisdiction in which Profile Connector is established.
            </p>
            <p>
              Notwithstanding the above, nothing in these Terms limits your
              rights as a consumer under the mandatory consumer protection laws
              of your country of residence.
            </p>
            <p>
              If any provision of these Terms is found to be unenforceable, the
              remaining provisions will remain in full force and effect.
            </p>
          </section>

          <section className="tos-section" id="contact">
            <h2>16. Contact Us</h2>
            <p>
              If you have any questions about these Terms, the Service, or our
              data practices, please contact us:
            </p>
            <div className="tos-contact-card">
              <p>
                <strong>Profile Connector</strong>
              </p>
              <p>
                For questions about these Terms of Service or your data, please
                reach out through the support page of the partner platform you
                are using, or contact us via the Chrome Web Store listing
                support link. We aim to respond to all inquiries within{" "}
                <strong>3 business days</strong>.
              </p>
              <p>
                For security-related reports, please describe the issue in
                detail so our team can respond appropriately. Security issues
                will be treated as urgent and prioritized accordingly.
              </p>
            </div>

            <div className="tos-highlight" style={{ marginTop: "32px" }}>
              <p>
                By using Profile Connector, you confirm that you have read and
                agreed to these Terms of Service. Thank you for using our
                extension responsibly.
              </p>
            </div>
          </section>
        </main>
      </div>

      <footer className="tos-footer">
        <p>
          © {new Date().getFullYear()} Profile Connector. All rights reserved.
          &nbsp;·&nbsp;{" "}
          <Link to="/landing">Home</Link>
          &nbsp;·&nbsp;{" "}
          <Link to="/tos">Terms of Service</Link>
          &nbsp;·&nbsp;{" "}
          <Link to="/privacy">Privacy Policy</Link>
        </p>
      </footer>
    </div>
  );
}
